/**
 * Simple Google Analytics helper functions
 */

// Simple helper to ensure gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Google Analytics utility object
const GoogleAnalytics = {
  /**
   * Track a page view
   * @param {string} path - The page path to track
   * @param {string} [title] - Optional page title
   */
  pageview: (path, title) => {
    if (!isGtagAvailable()) return;
    
    window.gtag('config', 'G-NEQ1RHQ06X', {
      page_path: path,
      page_title: title || document.title
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`GA Pageview: ${path}`);
    }
  },

  /**
   * Track an event
   * @param {string} action - The event action
   * @param {Object} [params] - Optional event parameters
   */
  event: (action, params = {}) => {
    if (!isGtagAvailable()) return;
    
    window.gtag('event', action, params);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`GA Event: ${action}`, params);
    }
  },

  /**
   * Track a purchase
   */
  trackPurchase: (purchaseData) => {
    if (!isGtagAvailable()) return;
    
    if (!purchaseData.transaction_id || !purchaseData.value) {
      return;
    }
    
    window.gtag('event', 'purchase', {
      transaction_id: purchaseData.transaction_id,
      value: purchaseData.value,
      currency: purchaseData.currency || 'USD',
      items: purchaseData.items || []
    });
  },

  /**
   * Set user properties
   * @param {Object} properties - User properties to set
   */
  setUserProperties: (properties) => {
    if (!isGtagAvailable()) return;
    
    window.gtag('set', 'user_properties', properties);
  },

  /**
   * Track form interactions (start, step completion, submission, abandonment)
   * @param {string} formName - Name of the form being tracked
   * @param {string} action - The interaction type (start, step, submit, abandon)
   * @param {Object} [params] - Additional parameters (step number, field name, etc.)
   */
  trackForm: (formName, action, params = {}) => {
    if (!isGtagAvailable()) return;
    
    const eventParams = {
      form_name: formName,
      form_action: action,
      ...params
    };
    
    window.gtag('event', 'form_interaction', eventParams);
  },

  /**
   * Track outbound link clicks
   * @param {string} url - The destination URL
   * @param {string} [linkText] - The text of the link
   * @param {string} [linkContext] - Where the link appears (header, footer, body, etc.)
   */
  trackOutboundLink: (url, linkText, linkContext) => {
    if (!isGtagAvailable()) return;
    
    // Only track external links
    if (url.startsWith('http') && !url.includes(window.location.hostname)) {
      window.gtag('event', 'outbound_link_click', {
        destination: url,
        link_text: linkText || url,
        link_context: linkContext || 'page_body',
        page: window.location.pathname
      });
    }
  },

  /**
   * Track scroll depth
   * @param {string} depth - The scroll depth percentage (25%, 50%, 75%, 100%)
   * @param {string} [pageId] - Optional identifier for the page
   */
  trackScrollDepth: (depth, pageId) => {
    if (!isGtagAvailable()) return;
    
    window.gtag('event', 'scroll_depth', {
      page: window.location.pathname,
      page_id: pageId || window.location.pathname,
      depth: depth
    });
  }
};

export default GoogleAnalytics; 